import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, DollarSign, Check, X, AlertCircle, Info } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useTenant } from '../contexts/TenantContext';
import { Currency } from '../types';

const CurrenciesTable: React.FC = () => {
  const { currentCompany } = useTenant();
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    symbol: '',
    description: '',
    is_active: true
  });

  // Verificar que solo Licitatrak pueda acceder
  const isSuperAdmin = currentCompany?.rut === '99.999.999-9';

  // Divisas obligatorias que no se pueden desactivar ni eliminar
  const requiredCurrencies = ['CLP', 'USD', 'EUR', 'CLF'];

  useEffect(() => {
    if (isSuperAdmin) {
      loadCurrencies();
    }
  }, [isSuperAdmin]);

  const loadCurrencies = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('currencies')
        .select('*')
        .order('code');

      if (error) throw error;
      setCurrencies(data || []);
    } catch (error: any) {
      console.error('Error loading currencies:', error);
      setError(error.message || 'Error al cargar divisas');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCurrencies = currencies.filter(currency =>
    currency.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    currency.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Validar código de divisa (solo letras mayúsculas, 3 caracteres)
      const codeRegex = /^[A-Z]{3}$/;
      if (!codeRegex.test(formData.code)) {
        throw new Error('El código de divisa debe ser de 3 letras mayúsculas (ej: USD, EUR, CLP)');
      }

      if (editingCurrency) {
        // No permitir cambiar el código de divisas existentes
        const { code, ...updateData } = formData;
        
        // No permitir desactivar divisas obligatorias
        if (requiredCurrencies.includes(editingCurrency.code) && !updateData.is_active) {
          throw new Error(`No se puede desactivar la divisa ${editingCurrency.code} porque es obligatoria`);
        }
        
        const { error } = await supabase
          .from('currencies')
          .update(updateData)
          .eq('id', editingCurrency.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('currencies')
          .insert([formData]);

        if (error) throw error;
      }

      await loadCurrencies();
      resetForm();
    } catch (error: any) {
      console.error('Error saving currency:', error);
      setError(error.message || 'Error al guardar divisa');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (currency: Currency) => {
    setEditingCurrency(currency);
    setFormData({
      code: currency.code,
      name: currency.name,
      symbol: currency.symbol,
      description: currency.description || '',
      is_active: currency.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (currency: Currency) => {
    // No permitir eliminar divisas obligatorias
    if (requiredCurrencies.includes(currency.code)) {
      setError(`No se puede eliminar la divisa ${currency.code} porque es obligatoria`);
      return;
    }

    if (window.confirm(`¿Estás seguro de que deseas eliminar la divisa ${currency.code}?`)) {
      try {
        setIsLoading(true);
        const { error } = await supabase
          .from('currencies')
          .delete()
          .eq('id', currency.id);

        if (error) throw error;
        await loadCurrencies();
      } catch (error: any) {
        console.error('Error deleting currency:', error);
        setError(error.message || 'Error al eliminar divisa');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const toggleCurrencyStatus = async (currency: Currency) => {
    // No permitir desactivar divisas obligatorias
    if (requiredCurrencies.includes(currency.code) && currency.is_active) {
      setError(`No se puede desactivar la divisa ${currency.code} porque es obligatoria`);
      return;
    }

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('currencies')
        .update({ is_active: !currency.is_active })
        .eq('id', currency.id);

      if (error) throw error;
      await loadCurrencies();
    } catch (error: any) {
      console.error('Error updating currency status:', error);
      setError(error.message || 'Error al actualizar estado de la divisa');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      symbol: '',
      description: '',
      is_active: true
    });
    setEditingCurrency(null);
    setShowForm(false);
    setError('');
  };

  if (!isSuperAdmin) {
    return (
      <div className="text-center py-12">
        <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Acceso Denegado</h3>
        <p className="text-gray-600">Solo Licitatrak puede acceder a la gestión de divisas globales.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Divisas</h1>
          <p className="text-gray-600">Administra las divisas disponibles en el sistema</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nueva Divisa
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingCurrency ? 'Editar Divisa' : 'Nueva Divisa'}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  required
                  disabled={!!editingCurrency}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  placeholder="USD"
                  maxLength={3}
                />
                {editingCurrency && (
                  <p className="text-xs text-gray-500 mt-1">El código de divisa no se puede modificar</p>
                )}
                {!editingCurrency && (
                  <p className="text-xs text-gray-500 mt-1">Código de 3 letras mayúsculas (ej: USD, EUR, CLP)</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Dólar Estadounidense"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Símbolo *
                </label>
                <input
                  type="text"
                  value={formData.symbol}
                  onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="$"
                  maxLength={5}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Descripción de la divisa"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={editingCurrency ? requiredCurrencies.includes(editingCurrency.code) : false}
                />
                <label htmlFor="is_active" className="ml-2 text-sm font-medium text-gray-700">
                  Divisa activa
                </label>
                {editingCurrency && requiredCurrencies.includes(editingCurrency.code) && (
                  <span className="ml-2 text-xs text-blue-600">
                    (Esta divisa es obligatoria y no puede ser desactivada)
                  </span>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isLoading ? 'Guardando...' : editingCurrency ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar divisas por código o nombre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 mb-1">Divisas Obligatorias</h4>
            <p className="text-sm text-blue-700">
              Las divisas CLP, USD, EUR y CLF son obligatorias para el funcionamiento del sistema y no pueden ser eliminadas ni desactivadas.
              Estas divisas son utilizadas en diferentes partes del sistema como licitaciones, productos y tasas de cambio.
            </p>
          </div>
        </div>
      </div>

      {/* Currencies List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Código
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Símbolo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descripción
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredCurrencies.length > 0 ? (
                filteredCurrencies.map((currency) => (
                  <tr key={currency.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="p-2 bg-blue-100 rounded-lg mr-3">
                          <DollarSign className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          {currency.code}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{currency.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{currency.symbol}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 line-clamp-2">{currency.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span 
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          currency.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {currency.is_active ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => toggleCurrencyStatus(currency)}
                          disabled={requiredCurrencies.includes(currency.code) && currency.is_active}
                          className={`p-2 rounded-lg transition-colors ${
                            requiredCurrencies.includes(currency.code) && currency.is_active
                              ? 'text-gray-300 cursor-not-allowed'
                              : currency.is_active
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-red-600 hover:bg-red-50'
                          }`}
                          title={
                            requiredCurrencies.includes(currency.code) && currency.is_active
                              ? 'Esta divisa es obligatoria y no puede ser desactivada'
                              : currency.is_active
                                ? 'Desactivar divisa'
                                : 'Activar divisa'
                          }
                        >
                          {currency.is_active ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => handleEdit(currency)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(currency)}
                          disabled={requiredCurrencies.includes(currency.code)}
                          className={`p-2 rounded-lg transition-colors ${
                            requiredCurrencies.includes(currency.code)
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                          }`}
                          title={
                            requiredCurrencies.includes(currency.code)
                              ? 'Esta divisa es obligatoria y no puede ser eliminada'
                              : 'Eliminar divisa'
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No hay divisas</h3>
                    <p className="text-gray-600 mb-6">
                      {searchTerm ? 'No se encontraron divisas con los filtros aplicados.' : 'Comienza agregando tu primera divisa.'}
                    </p>
                    {!searchTerm && (
                      <button
                        onClick={() => setShowForm(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                      >
                        Nueva Divisa
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CurrenciesTable;